/**
 * Shared fetch wrapper for calls to the app's own Go API.
 *
 * CRITICAL: `apiFetch`/`apiFetchRaw` must resolve the *global* `fetch` at call
 * time, never a reference captured at module load. `Session.init()` from
 * supertokens-auth-react monkey-patches `window.fetch` (see
 * node_modules/supertokens-website/lib/build/fetch.js) to attach
 * `credentials: 'include'` and to intercept 401s for session refresh + retry.
 * Below, `fetch(...)` is a bare identifier reference resolved against the
 * global scope on every call -- it is never assigned to a local/module-level
 * binding -- so whichever `fetch` is currently installed (SuperTokens-patched
 * or not, and regardless of init order relative to `Session.init()`) is
 * always the one that runs.
 *
 * `path` is whatever you'd pass as the first argument to `fetch()` -- in
 * practice the full URL the existing services already build from
 * `APP_CONFIG.apiUrl` (e.g. `${APP_CONFIG.apiUrl}/v1/teams/1`). This module
 * intentionally knows nothing about APP_CONFIG so it can't double-prefix a
 * URL a caller already qualified.
 */

export class ApiError extends Error {
    status: number;

    constructor(message: string, status: number) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
    }
}

async function parseErrorBody(response: Response): Promise<{ error?: string }> {
    try {
        return await response.json();
    } catch {
        return {};
    }
}

/**
 * Generalizes the `throwOnErrorResponse` helper that used to live only in
 * AdminService.js (guarding getEtlRuns/getEtlRun) into a shared helper every
 * service can use.
 */
async function throwIfNotOk(response: Response, path: string): Promise<Response> {
    if (response.ok) {
        return response;
    }
    const body = await parseErrorBody(response);
    throw new ApiError(
        body.error || `Request to ${path} failed (${response.status})`,
        response.status
    );
}

/**
 * Fetches `path` and returns the raw `Response`, throwing `ApiError` when the
 * response is not ok. Returning the raw Response (rather than parsed JSON) is
 * needed by callers that read response headers -- e.g.
 * `VideoService.getVideos`, which reads `X-Total-Count` for pagination.
 */
export async function apiFetchRaw(path: string, init?: RequestInit): Promise<Response> {
    const response = await fetch(path, init);
    return throwIfNotOk(response, path);
}

/**
 * Fetches `path`, parses the body as JSON, and returns it typed as `T`.
 * Throws `ApiError` when the response is not ok.
 */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await apiFetchRaw(path, init);
    if (response.status === 204) {
        return undefined as T;
    }
    return (await response.json()) as T;
}
